import axios from 'axios'
import type { IntrospectionResponse, GraphField } from '@/types'

export interface FieldInfo {
  name: string
  description?: string
  type: string
  kind: string
  isRelation: boolean
  isConnection?: boolean
  accessorKey?: string
  key?: string
  header?: string
  label?: string
  locked?: boolean
  graphql?: string
}

export async function fetchTypeFields(
  typeName: string,
  _customerNo: string,
  _tenantName: string,
): Promise<GraphField[]> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ignore = { _customerNo, _tenantName }
  console.log(`fetchTypeFields for ${typeName}`)
  const query = `
    query {
      __type(name: "${typeName}") {
        name
        kind
        fields {
          name
          description
          args {
            defaultValue
            name
            description
            type {
              description
              kind
              name
              ofType {
                name
                kind
                ofType {
                  name
                  kind
                }
              }
            }
          }
          type {
            name
            kind
            ofType {
              name
              kind
              ofType {
                name
                kind
                ofType {
                  name
                  kind
                  ofType {
                    name
                    kind
                  }
                }
              }
            }
          }
        }
      }
    }
  `

  try {
    const response = await axios.post<IntrospectionResponse>('/api/microtech/v1/proxy/graphql', {
      query,
    })

    const typeData = response?.data?.data?.__type
    console.log(
      `Introspektion von Typ ${typeName} (${typeData?.kind || 'UNKNOWN'}) erfolgreich, Felder:`,
      typeData?.fields?.length,
    )
    const fields = typeData?.fields
    if (!fields || fields.length === 0) {
      console.warn(
        `Typ ${typeName} hat keine Felder oder wurde nicht gefunden. Response:`,
        JSON.stringify(response?.data),
      )
    }
    return fields || []
  } catch (error) {
    console.error(`Fehler bei der Introspektion von Typ ${typeName}:`, error)
    return []
  }
}

export async function fetchTypeDescription(
  typeName: string,
  _customerNo: string,
  _tenantName: string,
): Promise<string | undefined> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ignore = { _customerNo, _tenantName }
  const query = `
    query {
      __type(name: "${typeName}") {
        description
      }
    }
  `
  try {
    const response = await axios.post<{ data: { __type: { description: string } } }>(
      '/api/microtech/v1/proxy/graphql',
      { query },
    )
    return response?.data?.data?.__type?.description
  } catch {
    return undefined
  }
}

interface IntrospectedTypeRef {
  name?: string | null
  kind?: string
  ofType?: IntrospectedTypeRef | null
}

/**
 * Entpackt LIST- und NON_NULL-Wrapper bis zum benannten Typ. microtech benennt auch die
 * Wrapper ("[AddressEdgeQuery]", "String!"), daher reicht ein fehlender Name als Kriterium nicht.
 */
function unwrapType<T extends IntrospectedTypeRef>(type: T): T {
  let current = type
  while (
    current &&
    (current.kind === 'LIST' || current.kind === 'NON_NULL' || !current.name) &&
    current.ofType
  ) {
    current = current.ofType as T
  }
  return current
}

/**
 * Holt Felder einer Tabelle nach dem microtech-Muster:
 * tblName -> NameTableQueryRead -> SingularNameRowQueryRead
 */
export async function fetchTableFields(
  tableName: string,
  customerNo: string,
  tenantName: string,
): Promise<FieldInfo[]> {
  // 1. Table-Type ermitteln (z.B. AddressesTableQueryRead für tblAddresses)
  // Das microtech-Muster ist: tbl{Name} -> {Name}TableQueryRead
  const namePart = tableName.startsWith('tbl') ? tableName.substring(3) : tableName
  const tableTypeName = `${namePart}TableQueryRead`
  console.log(`fetchTableFields for ${tableName}: Fetching tableTypeName=${tableTypeName}`)

  const tableFields = await fetchTypeFields(tableTypeName, customerNo, tenantName)

  // 2. Row-Type finden (normalerweise das 'edges' Feld -> node)
  const edgesField = tableFields.find((f) => f.name === 'edges')
  let nodeType = ''

  if (edgesField) {
    // Traverse nested types to find the actual named type
    const currentType = unwrapType(edgesField.type)

    if (currentType && (currentType.name === 'Edge' || currentType.name?.endsWith('EdgeQuery'))) {
      // We probably have a generic Edge type or a specific Edge type, look for 'node' field
      const edgeTypeName = currentType.name
      console.log(
        `fetchTableFields for ${tableName}: Found Edge type ${edgeTypeName}, fetching its node field`,
      )
      const edgeFields = await fetchTypeFields(edgeTypeName, customerNo, tenantName)
      const nodeField = edgeFields.find((f) => f.name === 'node')
      if (nodeField) {
        const nodeCurrentType = unwrapType(nodeField.type)
        nodeType = nodeCurrentType?.name || ''
      }
    } else {
      nodeType = currentType?.name || ''
    }
  }

  // Fallback: Suche in conRead
  if (!nodeType || nodeType.endsWith('ConnectionQuery')) {
    const conReadField = tableFields.find((f) => f.name === 'conRead')
    if (conReadField) {
      // conRead hat typ NameConnectionRead -> wir brauchen dessen edges -> node
      const currentType = unwrapType(conReadField.type)
      const connectionTypeName = currentType.name

      console.log(
        `fetchTableFields for ${tableName}: Searching in connectionTypeName=${connectionTypeName}`,
      )
      if (connectionTypeName) {
        const connectionFields = await fetchTypeFields(connectionTypeName, customerNo, tenantName)
        const cEdgesField = connectionFields.find((f) => f.name === 'edges')
        if (cEdgesField) {
          const eType = unwrapType(cEdgesField.type)
          nodeType = eType.name || ''

          if (nodeType === 'Edge' || nodeType.endsWith('EdgeQuery')) {
            const edgeTypeName = nodeType
            console.log(
              `fetchTableFields for ${tableName}: Found Edge type ${edgeTypeName} in conRead, fetching its node field`,
            )
            const edgeFields = await fetchTypeFields(edgeTypeName, customerNo, tenantName)
            const nodeField = edgeFields.find((f) => f.name === 'node')
            if (nodeField) {
              const nType = unwrapType(nodeField.type)
              nodeType = nType.name || ''
            }
          }
        }
      }
    }
  }

  // Fallback 2: Suche in rowRead
  if (!nodeType) {
    const rowReadField = tableFields.find((f) => f.name === 'rowRead')
    if (rowReadField) {
      const rType = unwrapType(rowReadField.type)
      nodeType = rType.name || ''
      console.log(`fetchTableFields for ${tableName}: Found nodeType in rowRead=${nodeType}`)
    }
  }

  // Fallback 3: Suche in rowsRead
  if (!nodeType) {
    const rowsReadField = tableFields.find((f) => f.name === 'rowsRead')
    if (rowsReadField) {
      const rType = unwrapType(rowsReadField.type)
      nodeType = rType.name || ''
      console.log(`fetchTableFields for ${tableName}: Found nodeType in rowsRead=${nodeType}`)
    }
  }

  let rowTypeName = nodeType || ''
  console.log(
    `fetchTableFields for ${tableName}: tableTypeName=${tableTypeName}, nodeType detected=${nodeType}`,
  )

  // Wenn das nicht klappt, versuchen wir das konventionelle Muster: {Singular}RowQueryRead
  if (!rowTypeName) {
    // Wenn wir tblAddresses haben, ist NamePart "Addresses".
    // Wir probieren den Singular "Address" + "RowQueryRead"
    const singular = namePart.endsWith('es')
      ? namePart.slice(0, -2)
      : namePart.endsWith('s')
        ? namePart.slice(0, -1)
        : namePart
    rowTypeName = `${singular}RowQueryRead`
    console.log(`fetchTableFields for ${tableName}: Fallback singular rowTypeName=${rowTypeName}`)
  }

  // Final check: Some microtech tables have direct RowQueryRead without connection
  if (!rowTypeName || rowTypeName === 'Unknown') {
    rowTypeName = tableTypeName.replace('TableQueryRead', 'RowQueryRead')
    console.log(`fetchTableFields for ${tableName}: Final fallback rowTypeName=${rowTypeName}`)
  }

  const rowFields = await fetchTypeFields(rowTypeName, customerNo, tenantName)
  console.log(
    `fetchTableFields for ${tableName}: rowTypeName=${rowTypeName}, rowFields count=${rowFields.length}`,
  )

  const resultFields: FieldInfo[] = []

  for (const f of rowFields) {
    const fType = unwrapType(f.type)

    const typeName = fType.name || 'Unknown'
    const isAco = typeName.startsWith('Aco') || f.name.startsWith('aco')
    let expanded = false

    if (isAco) {
      const effectiveTypeName =
        typeName && typeName !== 'Unknown' ? typeName : `Aco${f.name.substring(3)}`
      try {
        const subFields = await fetchTypeFields(effectiveTypeName, customerNo, tenantName)
        if (subFields && subFields.length > 0) {
          for (const sf of subFields) {
            if (sf.name.startsWith('_')) continue
            const combinedLabel =
              f.description && sf.description
                ? `${f.description}: ${sf.description}`
                : `${f.name}: ${sf.name}`
            resultFields.push({
              name: `${f.name}.${sf.name}`,
              description: sf.description || f.description,
              type: 'Scalar',
              kind: 'SCALAR',
              isRelation: false,
              isConnection: false,
              label: combinedLabel,
              header: combinedLabel,
              graphql: `${getGraphqlFieldName(f)} { ${getGraphqlFieldName(sf)} }`,
            })
          }
          expanded = true
        }
      } catch (e) {
        console.warn(
          `Could not fetch subfields for Aco field ${f.name} with type ${effectiveTypeName}`,
          e,
        )
      }
    }

    // Regular field (or Aco field that failed to expand)
    if (!expanded && !f.name.startsWith('aco')) {
      const label = f.description || f.name
      resultFields.push({
        name: f.name,
        accessorKey: f.name,
        description: f.description,
        type: typeName,
        kind: fType.kind || 'SCALAR',
        isRelation:
          f.name.startsWith('row') || f.name.startsWith('tbl') || f.name.startsWith('lnk'),
        isConnection: typeName.endsWith('ConnectionRead'),
        label: label,
        header: label,
        graphql: getGraphqlFieldName(f),
      })
    } else if (!expanded && f.name.startsWith('aco')) {
      // Fallback for aco fields that failed to expand - add them as a single field with default selection
      const label = f.description || f.name
      resultFields.push({
        name: f.name,
        accessorKey: f.name,
        description: f.description,
        type: typeName,
        kind: fType.kind || 'OBJECT',
        isRelation: false,
        isConnection: false,
        label: label,
        header: label,
        graphql: `${getGraphqlFieldName(f)} { totalNetAmount totalGrossAmount }`,
      })
    }
  }

  return resultFields
}

/**
 * Löst eine Relation auf (1:1, row...)
 */
export async function fetchRelationFields(
  rowTypeName: string,
  customerNo: string,
  tenantName: string,
): Promise<FieldInfo[]> {
  const fields = await fetchTypeFields(rowTypeName, customerNo, tenantName)
  const resultFields: FieldInfo[] = []

  for (const f of fields) {
    const fType = unwrapType(f.type)

    const typeName = fType.name || 'Unknown'
    const isAco = typeName.startsWith('Aco') || f.name.startsWith('aco')
    let expanded = false

    if (isAco) {
      const effectiveTypeName =
        typeName && typeName !== 'Unknown' ? typeName : `Aco${f.name.substring(3)}`
      try {
        const subFields = await fetchTypeFields(effectiveTypeName, customerNo, tenantName)
        if (subFields && subFields.length > 0) {
          for (const sf of subFields) {
            if (sf.name.startsWith('_')) continue
            const combinedLabel =
              f.description && sf.description
                ? `${f.description}: ${sf.description}`
                : `${f.name}: ${sf.name}`
            resultFields.push({
              name: `${f.name}.${sf.name}`,
              description: sf.description || f.description,
              type: 'Scalar',
              kind: 'SCALAR',
              isRelation: false,
              isConnection: false,
              label: combinedLabel,
              header: combinedLabel,
              graphql: `${getGraphqlFieldName(f)} { ${getGraphqlFieldName(sf)} }`,
            })
          }
          expanded = true
        }
      } catch (e) {
        console.warn(
          `Could not fetch subfields for Aco relation field ${f.name} with type ${effectiveTypeName}`,
          e,
        )
      }
    }

    if (!expanded && !f.name.startsWith('aco')) {
      const label = f.description || f.name
      resultFields.push({
        name: f.name,
        accessorKey: f.name,
        description: f.description,
        type: typeName,
        kind: fType.kind || 'SCALAR',
        isRelation:
          f.name.startsWith('row') || f.name.startsWith('tbl') || f.name.startsWith('lnk'),
        isConnection: typeName.endsWith('ConnectionRead'),
        label: label,
        header: label,
        graphql: getGraphqlFieldName(f),
      })
    } else if (!expanded && f.name.startsWith('aco')) {
      const label = f.description || f.name
      resultFields.push({
        name: f.name,
        accessorKey: f.name,
        description: f.description,
        type: typeName,
        kind: fType.kind || 'OBJECT',
        isRelation: false,
        isConnection: false,
        label: label,
        header: label,
        graphql: `${getGraphqlFieldName(f)} { totalNetAmount totalGrossAmount }`,
      })
    }
  }
  return resultFields
}

/**
 * Überprüft ob ein Typ eine Connection ist
 */
export async function isConnectionType(
  typeName: string,
  customerNo: string,
  tenantName: string,
): Promise<boolean> {
  if (typeName.endsWith('ConnectionRead')) return true
  const fields = await fetchTypeFields(typeName, customerNo, tenantName)
  if (!fields || fields.length === 0) return false
  return fields.some((f) => f.name === 'edges' || f.name === 'pageInfo')
}

function getGraphqlFieldName(f: GraphField): string {
  const rtfArg = f.args?.find((a) => a.name === 'as' && a.defaultValue === 'RTF_STRING')
  return rtfArg ? `${f.name}: ${f.name}(as:TEXT)` : f.name
}
